import { useCallback, useEffect, useState } from "react";
import { uuidv7 } from "../../../core/platform/id";
import { formatPesos, parsePesos, MoneyInputError } from "../money";
import { BooksRequestError, type BooksApi, type CapitalTable, type Partner, type PlaceId } from "./api";

/**
 * The partners' capital (T025, User Story 3, FR-133).
 *
 * Ownership is fixed by subscription and never moves with payment timing, so
 * this screen shows a share and a debt side by side without one implying the
 * other. Paying late costs a partner a debt, not a share.
 *
 * In-kind is shown separately from cash because it is a different kind of
 * promise kept: money the café never held, spent on its behalf.
 *
 * An advance is shown as a debt the café owes, never as capital: it is a loan,
 * and buys no share.
 */

const PLACES: Array<{ id: PlaceId; name: string }> = [
  { id: "capital_fund", name: "Capital fund" },
  { id: "ewallet", name: "E-wallet" },
  { id: "cash_held", name: "Cash held" },
];

const today = () => new Date().toISOString().slice(0, 10);

/** 3000 → "30%", 1250 → "12.5%". */
function ownership(bp: number): string {
  const percent = bp / 100;
  return `${Number.isInteger(percent) ? percent : percent.toFixed(2)}%`;
}

export interface CapitalProps {
  api: BooksApi;
  version: number;
  onChanged(): void;
}

export function Capital({ api, version, onChanged }: CapitalProps) {
  const [table, setTable] = useState<CapitalTable | null>(null);
  const [form, setForm] = useState<"contribution" | "call" | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setTable(await api.partners());
      setError(null);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Could not load the partners");
    }
  }, [api]);

  useEffect(() => {
    void load();
  }, [load, version]);

  const done = (message: string) => {
    setForm(null);
    setNotice(message);
    onChanged();
    void load();
  };

  if (form === "contribution" && table) {
    return <ContributionForm api={api} table={table} onDone={done} onCancel={() => setForm(null)} />;
  }
  if (form === "call" && table) {
    return <CapitalCallForm api={api} onDone={done} onCancel={() => setForm(null)} />;
  }

  return (
    <section className="books__panel">
      {error && (
        <p className="books__error" role="alert">
          {error}
        </p>
      )}
      {notice && (
        <p className="books__notice" role="status">
          {notice}
        </p>
      )}

      {!table ? (
        <p className="books__hint">Loading…</p>
      ) : (
        <>
          <ul className="books__list">
            {table.partners.map((partner) => (
              <li key={partner.id} className="books__row">
                <span className="books__row-name">
                  {partner.name}
                  <span className="books__chip">{ownership(partner.ownershipBp)}</span>
                  {partner.owed === 0 && <span className="books__chip">paid up</span>}
                </span>
                <span className="books__figure">₱{formatPesos(partner.paid)}</span>
                <span className="books__row-meta">
                  {partner.role ? `${partner.role} · ` : ""}
                  of ₱{formatPesos(partner.subscribed)} subscribed
                  {partner.inKind > 0 && `, ₱${formatPesos(partner.inKind)} of it in kind`}
                  {partner.owed > 0 && ` · still owes ₱${formatPesos(partner.owed)}`}
                  {partner.advanceOwed > 0 && ` · the café owes them ₱${formatPesos(partner.advanceOwed)} (advance)`}
                </span>
              </li>
            ))}
          </ul>

          <p className="books__total">
            Paid in <strong>₱{formatPesos(table.totals.paid)}</strong> of ₱
            {formatPesos(table.totals.subscribed)}
            {table.totals.owed > 0 && <> · still to come ₱{formatPesos(table.totals.owed)}</>}
            {table.totals.advanceOwed > 0 && (
              <> · advances to repay ₱{formatPesos(table.totals.advanceOwed)}</>
            )}
          </p>

          {table.calls.length > 0 && (
            <>
              <h3 className="books__subhead">Capital calls</h3>
              <ul className="books__list">
                {table.calls.map((call) => (
                  <li key={call.id} className="books__row">
                    <span className="books__row-name">
                      {call.title}
                      {call.outstanding === 0 && <span className="books__chip">settled</span>}
                    </span>
                    <span className="books__figure">₱{formatPesos(call.total)}</span>
                    <span className="books__row-meta">
                      {call.dueOn ? `Due ${call.dueOn} · ` : ""}₱{formatPesos(call.paid)} in
                      {call.outstanding > 0 && `, ₱${formatPesos(call.outstanding)} outstanding from `}
                      {call.outstanding > 0 &&
                        call.shares
                          .filter((share) => !share.settled)
                          .map((share) => table.partners.find((p) => p.id === share.partnerId)?.name ?? "someone")
                          .join(", ")}
                    </span>
                  </li>
                ))}
              </ul>
            </>
          )}

          <div className="books__buttons">
            <button type="button" className="books__action" onClick={() => setForm("call")}>
              Issue a call
            </button>
            <button type="button" className="books__action is-primary" onClick={() => setForm("contribution")}>
              Record contribution
            </button>
          </div>
        </>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------

interface FormProps {
  api: BooksApi;
  onDone(message: string): void;
  onCancel(): void;
}

function ContributionForm({ api, table, onDone, onCancel }: FormProps & { table: CapitalTable }) {
  /* Minted once: the confirmed retry carries the same id, so a contribution
   * refused for going beyond a subscription cannot land twice. */
  const [opId] = useState(() => uuidv7());
  const [partnerId, setPartnerId] = useState(table.partners[0]?.id ?? "");
  const [amount, setAmount] = useState("");
  const [place, setPlace] = useState<PlaceId>("capital_fund");
  const [contributedOn, setContributedOn] = useState(today());
  const [callId, setCallId] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** Set when the server refused for going past the subscription. */
  const [confirm, setConfirm] = useState<string | null>(null);

  const partner: Partner | undefined = table.partners.find((row) => row.id === partnerId);

  const submit = async (beyondSubscription = false) => {
    setError(null);

    let centavos: number;
    try {
      centavos = parsePesos(amount);
    } catch (caught) {
      setError(caught instanceof MoneyInputError ? caught.message : "That is not an amount.");
      return;
    }
    if (centavos === 0) {
      setError("Enter how much was paid in.");
      return;
    }

    setBusy(true);
    try {
      await api.contribute({
        opId,
        partnerId,
        amount: centavos,
        place,
        contributedOn,
        callId: callId || null,
        beyondSubscription,
      });
      onDone(`₱${formatPesos(centavos)} from ${partner?.name ?? "a partner"} recorded.`);
    } catch (caught) {
      if (caught instanceof BooksRequestError && caught.code === "BEYOND_SUBSCRIPTION") {
        // Nothing was written. Allowed, but only deliberately.
        setConfirm(caught.message);
      } else {
        setError(caught instanceof Error ? caught.message : "Could not record it");
      }
    } finally {
      setBusy(false);
    }
  };

  if (confirm) {
    return (
      <section className="books__panel">
        <h3 className="books__subhead">More than they subscribed for</h3>
        <p className="books__hint">
          {confirm} It changes what the others still owe, but not what anyone owns — ownership follows
          the subscription. Record it anyway?
        </p>
        <div className="books__buttons">
          <button type="button" className="books__action" onClick={() => setConfirm(null)} disabled={busy}>
            Go back
          </button>
          <button
            type="button"
            className="books__action is-primary"
            onClick={() => void submit(true)}
            disabled={busy}
          >
            Record it anyway
          </button>
        </div>
      </section>
    );
  }

  return (
    <section className="books__panel">
      <h3 className="books__subhead">Record a contribution</h3>
      <p className="books__hint">
        Cash paid in. Spending a partner paid for themselves is recorded as a purchase they paid,
        and counts here on its own.
      </p>

      <div className="books__pickers">
        <label className="books__field">
          <span>Partner</span>
          <select value={partnerId} onChange={(event) => setPartnerId(event.target.value)}>
            {table.partners.map((row) => (
              <option key={row.id} value={row.id}>
                {row.name}
              </option>
            ))}
          </select>
        </label>

        <label className="books__field">
          <span>How much (pesos)</span>
          <input value={amount} onChange={(event) => setAmount(event.target.value)} inputMode="decimal" autoFocus />
        </label>
      </div>

      {partner && partner.owed > 0 && (
        <p className="books__hint">
          {partner.name} still owes ₱{formatPesos(partner.owed)} of ₱{formatPesos(partner.subscribed)}.
        </p>
      )}

      <div className="books__pickers">
        <label className="books__field">
          <span>Into</span>
          <select value={place} onChange={(event) => setPlace(event.target.value as PlaceId)}>
            {PLACES.map((option) => (
              <option key={option.id} value={option.id}>
                {option.name}
              </option>
            ))}
          </select>
        </label>

        <label className="books__field">
          <span>When</span>
          <input type="date" value={contributedOn} onChange={(event) => setContributedOn(event.target.value)} />
        </label>
      </div>

      {table.calls.length > 0 && (
        <label className="books__field">
          <span>Against a call — optional</span>
          <select value={callId} onChange={(event) => setCallId(event.target.value)}>
            <option value="">Not against a call</option>
            {table.calls.map((call) => (
              <option key={call.id} value={call.id}>
                {call.title}
              </option>
            ))}
          </select>
        </label>
      )}

      {error && (
        <p className="books__error" role="alert">
          {error}
        </p>
      )}

      <div className="books__buttons">
        <button type="button" className="books__action" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
        <button type="button" className="books__action is-primary" onClick={() => void submit()} disabled={busy}>
          {busy ? "Saving…" : "Record"}
        </button>
      </div>
    </section>
  );
}

function CapitalCallForm({ api, onDone, onCancel }: FormProps) {
  const [opId] = useState(() => uuidv7());
  const [title, setTitle] = useState("");
  const [total, setTotal] = useState("");
  const [dueOn, setDueOn] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setError(null);

    let centavos: number;
    try {
      centavos = parsePesos(total);
    } catch (caught) {
      setError(caught instanceof MoneyInputError ? caught.message : "That is not an amount.");
      return;
    }
    if (centavos === 0) {
      setError("Enter how much is being called for.");
      return;
    }
    if (title.trim() === "") {
      setError("Say what the call is for.");
      return;
    }

    setBusy(true);
    try {
      await api.capitalCall({ opId, title: title.trim(), dueOn: dueOn || null, total: centavos, shares: null });
      onDone(`₱${formatPesos(centavos)} called for, split by ownership.`);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Could not issue it");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="books__panel">
      <h3 className="books__subhead">Issue a capital call</h3>
      <p className="books__hint">
        Split between the partners by ownership, to the centavo — the shares add up to exactly what is
        being asked for.
      </p>

      <label className="books__field">
        <span>What it is for</span>
        <input value={title} onChange={(event) => setTitle(event.target.value)} maxLength={200} autoFocus />
      </label>

      <div className="books__pickers">
        <label className="books__field">
          <span>How much in total (pesos)</span>
          <input value={total} onChange={(event) => setTotal(event.target.value)} inputMode="decimal" />
        </label>

        <label className="books__field">
          <span>Due — optional</span>
          <input type="date" value={dueOn} onChange={(event) => setDueOn(event.target.value)} />
        </label>
      </div>

      {error && (
        <p className="books__error" role="alert">
          {error}
        </p>
      )}

      <div className="books__buttons">
        <button type="button" className="books__action" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
        <button type="button" className="books__action is-primary" onClick={() => void submit()} disabled={busy}>
          {busy ? "Issuing…" : "Issue call"}
        </button>
      </div>
    </section>
  );
}
